import { Check, CircleDot, Clock, Minus, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { StatusTag } from '@/modules/settings';
import { cn } from '@/lib/utils';
import { isAfterTrigger, type ActionRecord, type RuleTrace, type RulesCatalog, type RunRecord, type StepResult } from '@/types/rules.types';
import { formatMs, RULE_STATUS_META, runDecider, runResult } from '../lib/labels';

const STEP_ICON: Record<StepResult | ActionRecord['status'], { icon: LucideIcon; className: string; label: string }> = {
	true: { icon: Check, className: 'bg-green-100 text-green-500', label: 'True' },
	false: { icon: X, className: 'bg-gray-100 text-gray-800', label: 'Not true' },
	done: { icon: Check, className: 'bg-green-100 text-green-500', label: 'Done' },
	would_do: { icon: CircleDot, className: 'bg-indigo-50 text-indigo-500', label: 'Would do' },
	skipped: { icon: Minus, className: 'bg-gray-100 text-gray-800', label: 'Skipped' },
	failed: { icon: TriangleAlert, className: 'bg-red-50 text-red-500', label: 'Failed' }
};

function TraceIcon({ result }: { result: StepResult | ActionRecord['status'] }) {
	const { icon: Icon, className, label } = STEP_ICON[result];
	return (
		<span className={cn('flex size-5 shrink-0 items-center justify-center rounded-full', className)} title={label}>
			<Icon className='size-3' aria-hidden />
			<span className='sr-only'>{label}</span>
		</span>
	);
}

const KIND_FALLBACK = { if: 'Check', action: 'Action', outcome: 'Decision' } as const;

interface RunTraceProps {
	run: RunRecord;
	catalog: RulesCatalog | undefined;
	/** A step's own words, when the playbook is known: "if the message is small talk is Greeting". */
	stepLabel?: (ruleId: string, stepId: string) => string | undefined;
	/** The Activity sheet also lists the actions, the facts read and the error. */
	detailed?: boolean;
}

function RuleBlock({ trace, stepLabel }: { trace: RuleTrace; stepLabel: RunTraceProps['stepLabel'] }) {
	return (
		<li className='flex flex-col gap-2 rounded-2xl border border-gray-300 bg-white p-3'>
			<div className='flex items-center gap-2'>
				<p className='min-w-0 flex-1 truncate text-[13px] font-medium leading-[18px] text-ink'>{trace.name}</p>
				<StatusTag tone={RULE_STATUS_META[trace.status].tone}>{RULE_STATUS_META[trace.status].label}</StatusTag>
			</div>
			{trace.steps.length === 0 ? (
				<p className='text-[12px] text-gray-800'>Did not run.</p>
			) : (
				<ol className='relative flex flex-col gap-2 before:absolute before:top-2 before:bottom-2 before:left-[9.5px] before:w-px before:bg-gray-300'>
					{trace.steps.map((step) => (
						<li key={step.stepId} className='relative flex items-start gap-2'>
							<TraceIcon result={step.result} />
							<div className='flex min-w-0 flex-col'>
								<span className='text-[12px] leading-5 text-ink'>{stepLabel?.(trace.ruleId, step.stepId) ?? KIND_FALLBACK[step.kind]}</span>
								{step.detail && <span className='text-[11px] leading-4 text-gray-800'>{step.detail}</span>}
							</div>
						</li>
					))}
				</ol>
			)}
		</li>
	);
}

/**
 * A run as a vertical trace: what happened to the message, the reply as the customer would see it,
 * then each playbook's steps with a tick, a cross or "would do". Shared by Test and Activity.
 */
export function RunTrace({ run, catalog, stepLabel, detailed = false }: RunTraceProps) {
	const result = runResult(run);
	const after = isAfterTrigger(run.trigger);
	const matched = runDecider(run);
	const ran = run.rules.filter((trace) => trace.steps.length > 0);
	const actionLabel = (id: string) => catalog?.actions.find((action) => action.id === id)?.label ?? id.replace(/_/g, ' ');
	return (
		<div className='flex flex-col gap-4'>
			<section aria-label='Result' className='flex flex-col gap-1 rounded-2xl border border-gray-300 bg-gray-50 px-4 py-3'>
				<div className='flex items-center gap-2'>
					<StatusTag tone={result.tone}>{run.dryRun ? result.wouldDo : result.done}</StatusTag>
					<span className='ml-auto inline-flex items-center gap-1 text-[11px] tabular-nums text-gray-800'>
						<Clock className='size-3' aria-hidden />
						{formatMs(run.timings.totalMs)} total
					</span>
				</div>
				<p className='text-[12px] leading-4 text-gray-900'>
					{after ? (
						matched ? (
							<>
								Ran after it happened: <span className='font-medium text-ink'>{matched}</span>.
							</>
						) : (
							'No playbook matched what happened, so nothing was done.'
						)
					) : run.decidedBy ? (
						<>
							Decided by <span className='font-medium text-ink'>{run.decidedBy.ruleName}</span>.
						</>
					) : (
						'No playbook decided, so the message goes to Advanced AI as usual.'
					)}
				</p>
			</section>

			{run.reply && (
				<section aria-label='Reply' className='flex flex-col items-end gap-1'>
					<div className='max-w-[90%] rounded-2xl rounded-br-md bg-ink px-3.5 py-2.5 text-[13px] leading-[19px] whitespace-pre-wrap text-white'>
						{run.reply.text}
					</div>
					<p className='text-[11px] leading-4 text-gray-800'>
						{run.reply.mode === 'ai' ? 'Written by Kim in your brand voice' : 'Your words'} ·{' '}
						{run.dryRun ? `Would reply in ${formatMs(run.reply.ms)}` : run.reply.sent ? `Replied in ${formatMs(run.reply.ms)}` : 'Not sent'}
						{run.reply.reason && ` · ${run.reply.reason}`}
					</p>
				</section>
			)}

			{run.error && (
				<p role='alert' className='rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] leading-4 text-red-600 wrap-anywhere'>
					{run.error}
				</p>
			)}

			<section aria-label='Steps' className='flex flex-col gap-2'>
				<p className='text-[12px] font-medium leading-4 text-gray-700'>Step by step</p>
				{ran.length === 0 ? (
					<p className='text-[12px] text-gray-800'>No playbook ran for this message.</p>
				) : (
					<ol className='flex flex-col gap-2'>
						{ran.map((trace) => (
							<RuleBlock key={trace.ruleId} trace={trace} stepLabel={stepLabel} />
						))}
					</ol>
				)}
			</section>

			{detailed && run.actions.length > 0 && (
				<section aria-label='Actions' className='flex flex-col gap-2'>
					<p className='text-[12px] font-medium leading-4 text-gray-700'>Actions</p>
					<ul className='flex flex-col gap-1.5'>
						{run.actions.map((action) => (
							<li key={`${action.ruleId}:${action.stepId}`} className='flex items-start gap-2 text-[12px] leading-5 text-ink'>
								<TraceIcon result={action.status} />
								<span className='min-w-0 flex-1'>
									{actionLabel(action.action)}
									{action.detail && <span className='block text-[11px] leading-4 text-gray-800'>{action.detail}</span>}
								</span>
								{action.ms !== undefined && <span className='shrink-0 tabular-nums text-[11px] text-gray-800'>{formatMs(action.ms)}</span>}
							</li>
						))}
					</ul>
				</section>
			)}

			{detailed && Object.keys(run.facts).length > 0 && (
				<section aria-label='What Kim read' className='flex flex-col gap-2'>
					<p className='text-[12px] font-medium leading-4 text-gray-700'>What Kim read</p>
					<dl className='flex flex-col'>
						{Object.entries(run.facts).map(([key, value]) => (
							<div key={key} className='grid grid-cols-[160px_1fr] gap-3 border-b border-gray-100 py-1.5 last:border-0'>
								<dt className='truncate text-[12px] text-gray-800'>{catalog?.facts.find((fact) => fact.id === key)?.label ?? key}</dt>
								<dd className='min-w-0 text-[12px] text-ink wrap-anywhere'>{typeof value === 'string' ? value : JSON.stringify(value)}</dd>
							</div>
						))}
					</dl>
				</section>
			)}

			{detailed && (
				<p className='text-[11px] tabular-nums text-gray-800'>
					Reading facts {formatMs(run.timings.factsMs)} · Actions {formatMs(run.timings.actionsMs)} · Total {formatMs(run.timings.totalMs)}
				</p>
			)}
		</div>
	);
}
